import type { ReactElement } from 'react';

import shouldForwardProp from '@styled-system/should-forward-prop';
import styled, { css } from 'styled-components';

import Header from '@virtuslab/nfs-shared/src/components/molecules/Header';
import palette from '@virtuslab/nfs-shared/src/theme/palette';

const variants = {
  subtle: {
    normal: {
      neutral: { color: palette.greyscale[10], text: 'secondary' },
      increase: { color: palette.support.success[10], text: 'success' },
      decrease: { color: palette.support.alert[10], text: 'alert' },
    },
    inverted: {
      neutral: { color: palette.inverted.white, text: 'secondary' },
      increase: { color: palette.inverted.white, text: 'success' },
      decrease: { color: palette.inverted.white, text: 'alert' },
    },
  },
  strong: {
    normal: {
      neutral: { color: palette.greyscale[60], text: 'primary' },
      increase: { color: palette.support.success[60], text: 'primary' },
      decrease: { color: palette.support.alert[60], text: 'primary' },
    },
    inverted: {
      neutral: { color: palette.inverted.white, text: 'secondary' },
      increase: { color: palette.inverted.white, text: 'success' },
      decrease: { color: palette.inverted.white, text: 'alert' },
    },
  },
} as const;

type Variant = keyof typeof variants;

const counterColors = ({ variant, inverted = false, type }: CounterProps) => css`
  background-color: ${variants[variant][inverted ? 'inverted' : 'normal'][type].color};
`;

type CounterProps = Omit<Props, 'value'>;

const StyledCounter = styled.div.withConfig({
  shouldForwardProp: (prop) => shouldForwardProp(prop),
})<CounterProps>`
  align-items: center;
  border-radius: ${({ theme }) => theme.scTheme.borders.radii.xl};
  display: flex;
  height: max-content;
  justify-content: center;
  ${counterColors};
  padding: ${({ theme }) => theme.scTheme.space[200]} ${({ theme }) => theme.scTheme.space[300]};
`;

export type Props = Readonly<{
  value: number | string;
  variant: Variant;
  type: 'decrease' | 'increase' | 'neutral';
  inverted?: boolean;
}>;

const Counter = ({
  value, inverted = false, type, variant,
}: Props): ReactElement => {
  const textVariant = variants[variant][inverted ? 'inverted' : 'normal'][type].text;
  const shouldInvertTextColor = variant === 'strong' && !inverted;

  return (
    <StyledCounter inverted={inverted} type={type} variant={variant}>
      <Header as="h7" inverted={shouldInvertTextColor} variant={textVariant}>
        {value}
      </Header>
    </StyledCounter>
  );
};

export default Counter;
